import { BudgetSections } from 'business/constants';
import { PivotFieldsAggridT } from 'business/definitions';
import { BudgetUserSettings } from '../hooks/budgetUserSettings';
import { FieldSettingsT } from 'routes/BudgetModule/config/types';

/**
 * Helper building a budget user settings body.
 * Takes modified grid patch data and places it into the appropriate user settings field.
 * @param section The section of the grid.
 * @param newGridsettings : the aggrid settings we want to place into the body.
 * @returns a partial BudgetUserSettings, ready to be used as patch data.
 */
const buildBudgetUserSettingsMutationBody = (
    section: BudgetSections,
    newGridsettings: PivotFieldsAggridT,
): Partial<BudgetUserSettings> =>
    section === BudgetSections.OPERATING
        ? {
              analyseDafOperatingGrid: {
                  columns: newGridsettings,
              },
          }
        : {
              analyseDafInvestmentGrid: {
                  columns: newGridsettings,
              },
          };

/**
 * Retrieves the appropriate settings for a given budget section.
 *
 * @param section - The section for which settings are requested.
 * @param budgetUserSettings - The user settings object containing settings for DAF Analysis grids.
 * @returns The settings corresponding to the specified budget section.
 */
export const getBudgetUserSettingsFieldBySection = (
    section: BudgetSections,
    budgetUserSettings: BudgetUserSettings,
): { columns: PivotFieldsAggridT } =>
    section === BudgetSections.OPERATING
        ? budgetUserSettings.analyseDafOperatingGrid
        : budgetUserSettings.analyseDafInvestmentGrid;

/**
 * Generates a callback function to update budget user settings based on a section.
 *
 * @param section - The section to update settings for.
 * @param settingsMutation - The function to mutate user settings.
 * @returns A callback function accepting pivot fields to update user settings accordingly.
 */
export const getUpdateBudgetUserSettingsCallback =
    (
        section: BudgetSections,
        settingsMutation: (arg1: Partial<BudgetUserSettings>) => void,
    ) =>
    (pivotFieldsAggrid: PivotFieldsAggridT) => {
        const body = buildBudgetUserSettingsMutationBody(
            section,
            pivotFieldsAggrid,
        );
        settingsMutation(body);
    };

/**
 * Generates a callback function to update budget user settings. No section is needed here : we use the common grid field. (SIFAC case)
 * @param settingsMutation
 */
export const getSifacUpdateBudgetSettingsCallback =
    (
        settingsMutation: (arg1: Partial<BudgetUserSettings>) => void,
    ): ((pivotFieldsAggrid: PivotFieldsAggridT) => void) =>
    (pivotFieldsAggrid: PivotFieldsAggridT) => {
        const body = {
            analyseDafCommonGrid: {
                columns: pivotFieldsAggrid,
            },
        };
        settingsMutation(body);
    };

/**
 * Generates a callback function to update budget user settings for all inscriptions.
 * @param settingsMutation
 */
export const getAllInscriptionsUpdateBudgetSettingsCallback =
    (
        settingsMutation: (arg1: Partial<BudgetUserSettings>) => void,
    ): ((allInscriptionsColumns: FieldSettingsT[]) => void) =>
    (allInscriptionsColumns: FieldSettingsT[]) => {
        const body = {
            allInscriptionsGrid: {
                columns: allInscriptionsColumns,
            },
        };
        settingsMutation(body);
    };
